import React, { useEffect, useMemo } from 'react';
import cn from 'classnames';
import type { UploadFile } from 'antd/es/upload/interface';
import { ReactComponent as CloseIcon } from 'dist/icons/close.svg';
import './commentAttachedFilesList.scss';

const isImageFile = (file: UploadFile) =>
  (file.type && String(file.type).startsWith('image/')) || /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.name || '');

const getExtensionLabel = (name?: string) => {
  if (!name || typeof name !== 'string') return 'FILE';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toUpperCase() : 'FILE';
};

interface CommentAttachedFilesListProps {
  value?: UploadFile[];
  onChange?: (files: UploadFile[]) => void;
}

/**
 * Renders files attached to the timeline comment form.
 * Images show a preview; other types show the file extension label.
 */
export const CommentAttachedFilesList: React.FC<CommentAttachedFilesListProps> = ({ value = [], onChange }) => {
  const previewUrlByUid = useMemo(() => {
    const map: Record<string, string> = {};
    value.forEach(file => {
      if (isImageFile(file) && file.originFileObj) {
        try {
          map[file.uid] = URL.createObjectURL(file.originFileObj);
        } catch {
          // ignore invalid blob
        }
      }
    });
    return map;
  }, [value]);

  useEffect(
    () => () => {
      Object.values(previewUrlByUid).forEach(url => URL.revokeObjectURL(url));
    },
    [previewUrlByUid]
  );

  const remove = uid => {
    onChange?.(value.filter(f => f.uid !== uid));
  };

  if (!value.length) return null;

  return (
    <ul className="comment-attached-files">
      {value.map(file => {
        const previewSrc = previewUrlByUid[file.uid] || file.thumbUrl || file.url;
        const showImage = isImageFile(file) && previewSrc;

        return (
          <li key={file.uid} className="comment-attached-files__item" title={file.name}>
            {showImage ? (
              <div className={cn('comment-attached-files__preview', 'comment-attached-files__preview--image')}>
                <img src={previewSrc} alt="" className="comment-attached-files__thumb" />
              </div>
            ) : (
              <div className={cn('comment-attached-files__preview', 'comment-attached-files__preview--file')}>
                <span className="comment-attached-files__ext">{getExtensionLabel(file.name)}</span>
              </div>
            )}
            <button
              type="button"
              className="comment-attached-files__remove"
              aria-label="Remove file"
              onClick={() => remove(file.uid)}
            >
              <CloseIcon />
            </button>
          </li>
        );
      })}
    </ul>
  );
};
